import React, { FC, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import IconButton from "@mui/material/IconButton";
import MuiButton from "@mui/material/Button";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import Modal from "@/components/Modal";
import Button from "@/components/Button";
import Select from "@/components/Select";
import { useAppTheme } from "@/utils/theme";
import { useUserStore } from "@/hooks/useUserStore";
import { useAvailableLanguagesSetting } from "@/hooks/useSetting";
import { useRewardsEngine } from "@/hooks/useRewardsEngine";
import {
  GoalTemplate,
  GoalTemplatePayload,
  GoalTemplateStatus,
} from "@/domain/goalTemplates.interface";
import goalTemplateValidationSchema, {
  GoalTemplateFormData,
} from "./validation.schema";
import StepFields from "./StepFields";

type GoalTemplateModalProps = {
  open: boolean;
  onClose: () => void;
  goalTemplate?: GoalTemplate;
  onSave: (data: GoalTemplatePayload) => Promise<void>;
  isPending: boolean;
};

const EMPTY_LANGUAGE = { language: "", title: "", description: "" };

const EMPTY_STEP = {
  name: "",
  status: "ACTIVE",
  order: undefined,
  rewardConfigurationId: "",
  languages: [] as { language: string; title: string; description: string }[],
};

const DEFAULT_VALUES: GoalTemplateFormData = {
  name: "",
  status: "ACTIVE",
  rewardConfigurationId: "",
  icon: "",
  languages: [],
  steps: [],
};

const GoalTemplateModal: FC<GoalTemplateModalProps> = ({
  open,
  onClose,
  goalTemplate,
  onSave,
  isPending,
}) => {
  const { t } = useTranslation("common");
  const theme = useAppTheme();
  const { userInfo } = useUserStore();
  const isEdit = !!goalTemplate;

  const availableLanguages = useAvailableLanguagesSetting();
  const { data: rewardConfigs, isLoading: isLoadingRewardConfigs } =
    useRewardsEngine(open);

  const languageOptions = useMemo(
    () => availableLanguages.map((code) => ({ key: code, value: code })),
    [availableLanguages]
  );

  const rewardConfigOptions = useMemo(
    () =>
      (rewardConfigs || []).map((rc) => ({
        key: rc._id,
        value: rc.event
          ? `${rc.event}${rc.eventType ? ` (${rc.eventType})` : ""}`
          : rc._id,
      })),
    [rewardConfigs]
  );

  const methods = useForm<GoalTemplateFormData>({
    resolver: yupResolver(goalTemplateValidationSchema(t)),
    mode: "all",
    defaultValues: DEFAULT_VALUES,
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = methods;

  const {
    fields: languageFields,
    append: appendLanguage,
    remove: removeLanguage,
  } = useFieldArray({
    control,
    name: "languages",
  });

  const {
    fields: stepFields,
    append: appendStep,
    remove: removeStep,
  } = useFieldArray({
    control,
    name: "steps",
  });

  useEffect(() => {
    if (goalTemplate) {
      reset({
        name: goalTemplate.name,
        status: (goalTemplate.status as GoalTemplateStatus) || "ACTIVE",
        rewardConfigurationId: goalTemplate.rewardConfigurationId || "",
        icon: goalTemplate.icon || "",
        languages: (goalTemplate.languages || []).map((l) => ({
          language: l.language,
          title: l.title,
          description: l.description || "",
        })),
        steps: (goalTemplate.steps || []).map((s) => ({
          name: s.name,
          status: s.status,
          order: s.order,
          rewardConfigurationId: s.rewardConfigurationId || "",
          languages: (s.languages || []).map((l) => ({
            language: l.language,
            title: l.title,
            description: l.description || "",
          })),
        })),
      });
    } else {
      reset(DEFAULT_VALUES);
    }
  }, [goalTemplate, reset]);

  const onSubmit = async (data: GoalTemplateFormData) => {
    const currentUserId = userInfo?._id as string;

    const languages = (data.languages || [])
      .filter((l) => l.language)
      .map((l) => ({
        language: l.language,
        title: (l.title || "").trim(),
        ...(l.description ? { description: l.description.trim() } : {}),
      }));

    const steps = (data.steps || []).map((s) => ({
      name: (s.name || "").trim(),
      status: s.status,
      ...(s.order !== undefined && s.order !== null ? { order: s.order } : {}),
      ...(s.rewardConfigurationId
        ? { rewardConfigurationId: s.rewardConfigurationId }
        : {}),
      languages: (s.languages || [])
        .filter((l) => l.language)
        .map((l) => ({
          language: l.language,
          title: (l.title || "").trim(),
          ...(l.description ? { description: l.description.trim() } : {}),
        })),
    }));

    const payload: GoalTemplatePayload = {
      name: data.name.trim(),
      status: data.status as GoalTemplateStatus,
      rewardConfigurationId: data.rewardConfigurationId,
      ...(data.icon ? { icon: data.icon.trim() } : {}),
      ...(languages.length > 0 ? { languages } : {}),
      ...(steps.length > 0 ? { steps } : {}),
      ...(isEdit ? { updatedBy: currentUserId } : { createdBy: currentUserId }),
    };
    await onSave(payload);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      isPending={isPending}
      title={isEdit ? "Edit Goal Template" : "New goal template"}
      theme={theme}
      size={{
        width: "100%",
        minHeight: 640,
        maxHeight: 800,
        maxWidth: 960,
      }}
      content={
        <FormProvider {...methods}>
          <Box sx={{ width: "100%", pt: "12px" }}>
            <Grid container spacing={"20px"}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="template-name"
                      label="Name"
                      variant="outlined"
                      required
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.name}
                      helperText={errors.name ? errors.name.message : null}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <Select
                      handleChange={(value) => field.onChange(value)}
                      data={[
                        { key: "ACTIVE", value: "Active" },
                        { key: "INACTIVE", value: "Inactive" },
                      ]}
                      value={field.value as string}
                      label="Status"
                      id="template-status"
                      required
                      error={!!errors.status}
                      errorMessage={errors.status?.message || ""}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <Controller
                  name="rewardConfigurationId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      handleChange={(value) => field.onChange(value)}
                      data={rewardConfigOptions}
                      value={field.value as string}
                      label="Reward configuration"
                      id="template-reward-config"
                      required
                      isLoading={isLoadingRewardConfigs}
                      error={!!errors.rewardConfigurationId}
                      errorMessage={errors.rewardConfigurationId?.message || ""}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller
                  name="icon"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="template-icon"
                      label="Icon"
                      variant="outlined"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      fullWidth
                    />
                  )}
                />
              </Grid>

              {/* Template-level languages */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography variant="titleSmall">
                    Template languages
                  </Typography>
                  <MuiButton
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => appendLanguage(EMPTY_LANGUAGE)}
                  >
                    Add language
                  </MuiButton>
                </Box>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  {languageFields.map((langField, langIndex) => {
                    const langErrors = errors.languages?.[langIndex];
                    return (
                      <Grid container spacing={"12px"} key={langField.id}>
                        <Grid item xs={12} sm={3}>
                          <Controller
                            name={`languages.${langIndex}.language` as const}
                            control={control}
                            render={({ field }) => (
                              <Select
                                handleChange={(value) => field.onChange(value)}
                                data={languageOptions}
                                value={field.value as string}
                                label="Language"
                                id={`template-lang-${langIndex}`}
                                required
                                error={!!langErrors?.language}
                                errorMessage={
                                  langErrors?.language?.message || ""
                                }
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Controller
                            name={`languages.${langIndex}.title` as const}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                id={`template-title-${langIndex}`}
                                label="Title"
                                variant="outlined"
                                required
                                value={field.value}
                                onChange={(value) => field.onChange(value)}
                                error={!!langErrors?.title}
                                helperText={langErrors?.title?.message || null}
                                fullWidth
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={11} sm={4}>
                          <Controller
                            name={`languages.${langIndex}.description` as const}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                id={`template-desc-${langIndex}`}
                                label="Description"
                                variant="outlined"
                                value={field.value}
                                onChange={(value) => field.onChange(value)}
                                fullWidth
                              />
                            )}
                          />
                        </Grid>
                        <Grid
                          item
                          xs={1}
                          sm={1}
                          sx={{ display: "flex", alignItems: "center" }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => removeLanguage(langIndex)}
                            aria-label="remove language"
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Grid>
                      </Grid>
                    );
                  })}
                </Box>
              </Grid>

              {/* Steps */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography variant="titleSmall">Steps</Typography>
                  <MuiButton
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => appendStep(EMPTY_STEP)}
                  >
                    Add step
                  </MuiButton>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {stepFields.map((stepField, index) => (
                    <StepFields
                      key={stepField.id}
                      stepIndex={index}
                      onRemove={() => removeStep(index)}
                      canRemove={true}
                      languageOptions={languageOptions}
                      rewardConfigOptions={rewardConfigOptions}
                      isLoadingRewardConfigs={isLoadingRewardConfigs}
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </FormProvider>
      }
      actions={
        <Box sx={{ display: "flex", gap: "10px" }}>
          <Button
            variantType="cancel"
            onClick={onClose}
            disabled={isPending}
            label="CANCEL"
          />
          <Button
            variantType="save"
            onClick={() => handleSubmit(onSubmit)()}
            disabled={isPending}
            isLoading={isPending}
            label="SAVE"
          />
        </Box>
      }
    />
  );
};

export default GoalTemplateModal;
